import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play, XCircle, CheckCircle, Clock, AlertTriangle, Workflow, Plus, X,
  Truck, Package, Zap, Gift, RotateCcw, AlertOctagon, ChevronRight,
  User, Calendar, TrendingUp, Activity, Search, Filter as FilterIcon, Building2
} from 'lucide-react';
import api from '../services/api';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';

// Workflow type configurations with business-friendly descriptions
const WORKFLOW_TYPES = {
  STANDARD: {
    icon: Package,
    color: 'blue',
    title: 'Standard Material',
    description: 'Regular supplier deliveries with full QC inspection',
    whenToUse: 'Normal shipments from trusted suppliers',
    sla: '3.4 hours',
    qcRequired: true,
    steps: ['Truck Arrival', 'Gate Check', 'Dock Assigned', 'Unloading', 'QC Inspection', 'GRN Creation', 'SAP Posted'],
    features: ['Full quality check', 'Standard priority', 'Complete documentation']
  },
  JIT: {
    icon: Zap,
    color: 'orange',
    title: 'Just-In-Time (JIT)',
    description: 'Urgent materials needed immediately on production line',
    whenToUse: 'Critical materials needed for active production',
    sla: '55 minutes',
    qcRequired: false,
    steps: ['Truck Arrival', 'Gate Check', 'Priority Dock', 'Fast Unloading', 'Direct to Production', 'SAP Posted'],
    features: ['Highest priority', 'QC bypassed', 'Express handling']
  },
  FOC: {
    icon: Gift,
    color: 'green',
    title: 'Free of Charge (FOC)',
    description: 'Free samples, promotional items, or customer returns',
    whenToUse: 'Non-invoice materials and samples',
    sla: '1.4 hours',
    qcRequired: false,
    steps: ['Truck Arrival', 'Gate Check', 'Dock Assigned', 'Quick Unloading', 'GRN Creation', 'SAP Posted'],
    features: ['No invoice', 'Simple process', 'Minimal checks']
  },
  RETURN: {
    icon: RotateCcw,
    color: 'yellow',
    title: 'Return Material',
    description: 'Defective materials being returned to supplier',
    whenToUse: 'Outbound returns and rejections',
    sla: '2.5 hours',
    qcRequired: true,
    steps: ['Gate Entry', 'Quality Documentation', 'Packaging', 'Loading', 'Exit Gate'],
    features: ['Defect documentation', 'Return paperwork', 'Supplier notification']
  },
  CRITICAL: {
    icon: AlertOctagon,
    color: 'red',
    title: 'Critical Supplier',
    description: 'Materials from suppliers with quality concerns (Rating < 2.5)',
    whenToUse: 'Shipments from suppliers on quality watch',
    sla: '5.75 hours',
    qcRequired: true,
    steps: ['Truck Arrival', 'Gate Check', 'Isolation Dock', '100% QC Inspection', 'Extended Verification', 'GRN Creation', 'SAP Posted'],
    features: ['Enhanced inspection', 'Full batch testing', 'Quality hold']
  }
};

const WorkflowEngineImproved = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState(null);
  const [expandedInstance, setExpandedInstance] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    workflowType: 'all',
    slaStatus: 'all'
  });
  const [createForm, setCreateForm] = useState({
    truckArrivalId: '',
    supplierId: '',
    materialType: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  // Fetch workflow instances
  const { data: instances = [], isLoading: loadingInstances } = useQuery({
    queryKey: ['workflow-instances'],
    queryFn: async () => {
      const response = await api.get('/workflow/instances/');
      return response.data?.results || response.data || [];
    },
  });

  // Fetch workflow definitions (disabled - not needed for now)
  const { data: definitions = [] } = useQuery({
    queryKey: ['workflow-definitions'],
    queryFn: async () => {
      return [];
    },
    enabled: false, // Disable this query for now
  });

  // Fetch statistics (disabled until endpoint is implemented)
  const { data: stats } = useQuery({
    queryKey: ['workflow-stats'],
    queryFn: async () => {
      // Endpoint not yet implemented, return empty stats
      return {};
    },
    enabled: false, // Disable this query for now
  });

  // Fetch trucks for dropdown
  const { data: trucks = [] } = useQuery({
    queryKey: ['gate-trucks'],
    queryFn: async () => {
      const response = await api.get('/gate/trucks/');
      return response.data?.results || response.data || [];
    },
  });

  // Fetch suppliers for dropdown
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const response = await api.get('/procurement/suppliers/?is_active=true');
      return response.data?.results || response.data || [];
    },
  });

  // Material type options
  const materialTypeOptions = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'components', label: 'Components' },
    { value: 'packaging', label: 'Packaging Material' },
    { value: 'spare_parts', label: 'Spare Parts' },
    { value: 'finished_goods', label: 'Finished Goods' },
  ];

  const getSLAStatus = (instance) => {
    // Check if SLA is breached
    if (instance.sla_breached) return 'breached';

    // Use sla_percentage from API to determine status
    const percentUsed = instance.sla_percentage || 0;

    if (percentUsed > 80) return 'at-risk';
    if (percentUsed >= 0) return 'on-track';

    return 'unknown';
  };

  const getSLAColor = (status) => {
    switch (status) {
      case 'on-track': return 'text-green-600 bg-green-50 border-green-200';
      case 'at-risk': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'breached': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getProgressPercentage = (instance) => {
    // Map current_state to progress percentage
    const stateProgressMap = {
      'ARRIVED': 10,
      'GATE_CHECK': 20,
      'DOCK_ASSIGNED': 35,
      'UNLOADING': 50,
      'QC_INSPECTION': 65,
      'GRN_CREATION': 80,
      'SAP_POSTED': 95,
      'COMPLETED': 100,
      'REJECTED': 0,
      'ON_HOLD': 0,
      'EXCEPTION': 0,
    };
    return stateProgressMap[instance.current_state] || 0;
  };

  const getStepStatus = (stepName, currentState) => {
    // Map step names to workflow states
    const stepStateMap = {
      'Truck Arrival': ['ARRIVED'],
      'Gate Entry': ['ARRIVED'],
      'Gate Check': ['GATE_CHECK'],
      'Dock Assigned': ['DOCK_ASSIGNED'],
      'Priority Dock': ['DOCK_ASSIGNED'],
      'Isolation Dock': ['DOCK_ASSIGNED'],
      'Unloading': ['UNLOADING'],
      'Fast Unloading': ['UNLOADING'],
      'Quick Unloading': ['UNLOADING'],
      'QC Inspection': ['QC_INSPECTION'],
      '100% QC Inspection': ['QC_INSPECTION'],
      'Extended Verification': ['QC_INSPECTION'],
      'GRN Creation': ['GRN_CREATION'],
      'SAP Posted': ['SAP_POSTED'],
      'Direct to Production': ['SAP_POSTED'],
      'Quality Documentation': ['GATE_CHECK'],
      'Packaging': ['DOCK_ASSIGNED'],
      'Loading': ['UNLOADING'],
      'Exit Gate': ['COMPLETED'],
      'Quarantine Area': ['GRN_CREATION'],
      'Conditional Release': ['SAP_POSTED'],
    };

    // State progression order
    const stateOrder = ['ARRIVED', 'GATE_CHECK', 'DOCK_ASSIGNED', 'UNLOADING', 'QC_INSPECTION', 'GRN_CREATION', 'SAP_POSTED', 'COMPLETED'];

    const stepStates = stepStateMap[stepName] || [];
    const currentStateIndex = stateOrder.indexOf(currentState);

    // Check if any of the step's states match or are before current state
    const stepMaxIndex = Math.max(...stepStates.map(s => stateOrder.indexOf(s)));
    const stepMinIndex = Math.min(...stepStates.map(s => stateOrder.indexOf(s)));

    if (currentStateIndex > stepMaxIndex) {
      return 'completed';
    } else if (currentStateIndex >= stepMinIndex && currentStateIndex <= stepMaxIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const handleWorkflowTypeSelect = (type) => {
    setSelectedWorkflowType(type);
  };

  const handleTruckChange = (e) => {
    const truckId = e.target.value;
    setCreateForm(prev => ({ ...prev, truckArrivalId: truckId }));

    // Auto-populate supplier if truck is selected
    if (truckId) {
      const selectedTruck = trucks.find(t => t.id === parseInt(truckId));
      if (selectedTruck?.supplier?.id) {
        setCreateForm(prev => ({ ...prev, supplierId: selectedTruck.supplier.id.toString() }));
      }
    }
  };

  const createWorkflowMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/workflow/instances/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-instances']);
      queryClient.invalidateQueries(['workflow-stats']);
      setShowCreateModal(false);
      setSelectedWorkflowType(null);
      setCreateForm({ truckArrivalId: '', supplierId: '', materialType: '', notes: '' });
      setIsSubmitting(false);
      success('Workflow started successfully!');
    },
    onError: (err) => {
      setIsSubmitting(false);
      showError(err.response?.data?.error || 'Failed to create workflow');
    }
  });

  const handleCreateWorkflow = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const context = {};
    if (selectedWorkflowType === 'JIT') {
      context.is_jit = true;
      context.qc_required = false;
    } else if (selectedWorkflowType === 'FOC') {
      context.is_foc = true;
      context.qc_required = false;
    } else if (selectedWorkflowType === 'RETURN') {
      context.is_return = true;
    } else if (selectedWorkflowType === 'CRITICAL') {
      context.supplier_rating = 'D';
      context.qc_required = true;
    } else {
      context.qc_required = true;
    }

    // Find truck and supplier info
    const selectedTruck = trucks.find(t => t.id === parseInt(createForm.truckArrivalId));
    const selectedSupplier = suppliers.find(s => s.id === parseInt(createForm.supplierId));

    createWorkflowMutation.mutate({
      object_type: 'truck_arrival',
      object_id: createForm.truckArrivalId || Math.floor(Math.random() * 10000),
      context: {
        ...context,
        truck_number: selectedTruck?.truck_number || '',
        supplier_name: selectedSupplier?.name || '',
        material_type: createForm.materialType,
        notes: createForm.notes
      },
      auto_start: true
    });
  };

  const startWorkflowMutation = useMutation({
    mutationFn: async (instanceId) => {
      const response = await api.post(`/workflow/instances/${instanceId}/start/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-instances']);
      success('Workflow started successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to start workflow');
    }
  });

  const cancelWorkflowMutation = useMutation({
    mutationFn: async (instanceId) => {
      const response = await api.post(`/workflow/instances/${instanceId}/cancel/`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['workflow-instances']);
      queryClient.invalidateQueries(['workflow-stats']);
      success('Workflow cancelled successfully!');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to cancel workflow');
    }
  });

  const handleStartWorkflow = (instanceId) => {
    startWorkflowMutation.mutate(instanceId);
  };

  const handleCancelWorkflow = (instanceId) => {
    if (window.confirm('Are you sure you want to cancel this workflow?')) {
      cancelWorkflowMutation.mutate(instanceId);
    }
  };

  // Filter instances
  const filteredInstances = instances.filter(instance => {
    const matchesSearch = !filters.search ||
      instance.workflow_id?.toLowerCase().includes(filters.search.toLowerCase()) ||
      instance.truck_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
      instance.driver_name?.toLowerCase().includes(filters.search.toLowerCase());

    // Map is_active to status for filtering
    const instanceStatus = instance.is_active
      ? (instance.current_state === 'COMPLETED' ? 'COMPLETED' : 'IN_PROGRESS')
      : 'COMPLETED';

    const matchesStatus = filters.status === 'all' || instanceStatus === filters.status;
    const matchesWorkflowType = filters.workflowType === 'all' || instance.workflow_type === filters.workflowType;

    const slaStatus = getSLAStatus(instance);
    const matchesSLA = filters.slaStatus === 'all' || slaStatus === filters.slaStatus;

    const matchesTab = activeTab === 'active'
      ? instance.is_active && instance.current_state !== 'COMPLETED'
      : activeTab === 'completed'
      ? !instance.is_active || instance.current_state === 'COMPLETED'
      : true;

    return matchesSearch && matchesStatus && matchesWorkflowType && matchesSLA && matchesTab;
  });

  // Filter options
  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'PENDING', label: 'Pending' },
        { value: 'IN_PROGRESS', label: 'In Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'FAILED', label: 'Failed' },
        { value: 'CANCELLED', label: 'Cancelled' }
      ]
    },
    {
      name: 'workflowType',
      label: 'Workflow Type',
      value: filters.workflowType,
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'STANDARD', label: 'Standard Material' },
        { value: 'JIT', label: 'Just-In-Time' },
        { value: 'FOC', label: 'Free of Charge' },
        { value: 'RETURN', label: 'Return Material' },
        { value: 'CRITICAL', label: 'Critical Supplier' }
      ]
    },
    {
      name: 'slaStatus',
      label: 'SLA Status',
      value: filters.slaStatus,
      options: [
        { value: 'all', label: 'All SLA Statuses' },
        { value: 'on-track', label: 'On Track' },
        { value: 'at-risk', label: 'At Risk' },
        { value: 'breached', label: 'Breached' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Workflow className="w-8 h-8 text-blue-600" />
              Material Workflow Tracker
            </h1>
            <p className="text-gray-600 mt-1">Track shipments from gate entry to warehouse</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
          >
            <Plus className="w-5 h-5" />
            New Arrival
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Shipments</p>
                <p className="text-3xl font-bold text-blue-600">{stats.in_progress || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Currently processing</p>
              </div>
              <Activity className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed Today</p>
                <p className="text-3xl font-bold text-green-600">{stats.completed || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Finished workflows</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SLA Breaches</p>
                <p className="text-3xl font-bold text-red-600">{stats.sla_breached || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Exceeded time limits</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-500 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg. Processing Time</p>
                <p className="text-3xl font-bold text-purple-600">
                  {stats.avg_duration ? formatDuration(stats.avg_duration) : '-'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Per shipment</p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-500 opacity-20" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between p-4 gap-4">
            <nav className="flex gap-2">
              {[
                { id: 'active', label: 'Active', icon: Activity },
                { id: 'completed', label: 'Completed', icon: CheckCircle },
                { id: 'all', label: 'All Workflows', icon: Workflow }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-600 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* FilterSection */}
        <div className="p-4 border-b border-gray-200">
          <FilterSection
            filters={filterOptions}
            onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
            onClearFilters={() => setFilters({ search: '', status: 'all', workflowType: 'all', slaStatus: 'all' })}
            searchValue={filters.search}
            searchPlaceholder="Search by truck, supplier, or workflow ID..."
          />
        </div>

        {/* Workflow Cards */}
        <div className="p-6">
          {loadingInstances ? (
            <LoadingState message="Loading workflows..." />
          ) : filteredInstances.length === 0 ? (
            <EmptyState
              icon={Workflow}
              title="No workflows found"
              message="Try adjusting your filters or create a new workflow"
            />
          ) : (
            <div className="space-y-4">
              {filteredInstances.map((instance) => {
                const workflowType = WORKFLOW_TYPES[instance.workflow_type] || WORKFLOW_TYPES.STANDARD;
                const Icon = workflowType.icon;
                const slaStatus = getSLAStatus(instance);
                const progress = getProgressPercentage(instance);
                const isExpanded = expandedInstance === instance.id;

                return (
                  <div
                    key={instance.id}
                    className="bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-all duration-200"
                  >
                    {/* Card Header */}
                    <div
                      className="p-5 cursor-pointer"
                      onClick={() => setExpandedInstance(isExpanded ? null : instance.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Workflow Type Icon */}
                          <div className={`p-3 rounded-lg bg-${workflowType.color}-50`}>
                            <Icon className={`w-6 h-6 text-${workflowType.color}-600`} />
                          </div>

                          {/* Main Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {instance.context?.truck_number || instance.instance_id}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${workflowType.color}-100 text-${workflowType.color}-700`}>
                                {workflowType.title}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                instance.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                instance.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                                instance.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {instance.status}
                              </span>
                            </div>

                            {instance.context?.supplier_name && (
                              <p className="text-sm text-gray-600 mb-3">
                                <span className="font-medium">Supplier:</span> {instance.context.supplier_name}
                              </p>
                            )}

                            {/* Progress Bar */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-gray-600">Progress</span>
                                <span className="font-medium text-gray-900">{progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full bg-${workflowType.color}-600 transition-all duration-500`}
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                            </div>

                            {/* Current Step */}
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-600">
                                {instance.current_state?.replace(/_/g, ' ') || 'Waiting to start'} •
                                {' '}{instance.elapsed_time || '0m'} elapsed
                              </span>
                            </div>
                          </div>

                          {/* SLA Status */}
                          <div className={`px-4 py-2 rounded-lg border ${getSLAColor(slaStatus)}`}>
                            <p className="text-xs font-medium uppercase">SLA Status</p>
                            <p className="text-sm font-bold mt-1 capitalize">{slaStatus.replace('-', ' ')}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {instance.status === 'PENDING' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartWorkflow(instance.id);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Start Workflow"
                              disabled={startWorkflowMutation.isLoading}
                            >
                              <Play className="w-5 h-5" />
                            </button>
                          )}
                          {['PENDING', 'IN_PROGRESS'].includes(instance.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelWorkflow(instance.id);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Cancel Workflow"
                              disabled={cancelWorkflowMutation.isLoading}
                            >
                              <XCircle className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 p-5 bg-gray-50">
                        <h4 className="font-semibold text-gray-900 mb-4">Workflow Steps</h4>
                        <div className="space-y-3">
                          {workflowType.steps.map((step, index) => {
                            const stepStatus = getStepStatus(step, instance.current_state);
                            const isCompleted = stepStatus === 'completed';
                            const isCurrent = stepStatus === 'current';

                            return (
                              <div
                                key={index}
                                className={`flex items-center gap-4 p-3 rounded-lg ${
                                  isCurrent ? 'bg-blue-50 border-2 border-blue-300' :
                                  isCompleted ? 'bg-green-50' :
                                  'bg-white'
                                }`}
                              >
                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                  isCompleted ? 'bg-green-500' :
                                  isCurrent ? 'bg-blue-500 animate-pulse' :
                                  'bg-gray-300'
                                }`}>
                                  {isCompleted ? (
                                    <CheckCircle className="w-5 h-5 text-white" />
                                  ) : isCurrent ? (
                                    <Clock className="w-5 h-5 text-white" />
                                  ) : (
                                    <span className="text-white text-sm">{index + 1}</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium ${isCurrent ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {step}
                                  </p>
                                  {isCurrent && (
                                    <p className="text-sm text-blue-600">In progress...</p>
                                  )}
                                  {isCompleted && (
                                    <p className="text-sm text-green-600">Completed</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Start New Material Workflow</h2>
                  <p className="text-gray-600 mt-1">Select the appropriate workflow type for this shipment</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedWorkflowType(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {!selectedWorkflowType ? (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Workflow Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(WORKFLOW_TYPES).map(([key, type]) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => handleWorkflowTypeSelect(key)}
                        className={`text-left p-6 rounded-lg border-2 hover:border-${type.color}-500 hover:bg-${type.color}-50 transition-all duration-200`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg bg-${type.color}-100`}>
                            <Icon className={`w-8 h-8 text-${type.color}-600`} />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">{type.title}</h4>
                            <p className="text-sm text-gray-600 mb-3">{type.description}</p>
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className={`px-2 py-1 bg-${type.color}-100 text-${type.color}-700 rounded text-xs font-medium`}>
                                SLA: {type.sla}
                              </span>
                              <span className={`px-2 py-1 ${type.qcRequired ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'} rounded text-xs font-medium`}>
                                QC: {type.qcRequired ? 'Required' : 'Not Required'}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">When to use: {type.whenToUse}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateWorkflow} className="p-6">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    {(() => {
                      const type = WORKFLOW_TYPES[selectedWorkflowType];
                      const Icon = type.icon;
                      return (
                        <>
                          <div className={`p-2 rounded-lg bg-${type.color}-100`}>
                            <Icon className={`w-6 h-6 text-${type.color}-600`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{type.title}</h3>
                            <p className="text-sm text-gray-600">{type.description}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedWorkflowType(null)}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    ← Change workflow type
                  </button>
                </div>

                <div className="space-y-4">
                  <SmartDropdown
                    label="Truck Number"
                    name="truckArrivalId"
                    value={createForm.truckArrivalId}
                    onChange={handleTruckChange}
                    options={trucks.map(truck => ({
                      value: truck.id,
                      label: `${truck.truck_number} - ${truck.driver?.name || 'No Driver'}`
                    }))}
                    icon={Truck}
                    required
                    placeholder="Select truck"
                  />

                  <SmartDropdown
                    label="Supplier"
                    name="supplierId"
                    value={createForm.supplierId}
                    onChange={(e) => setCreateForm({ ...createForm, supplierId: e.target.value })}
                    options={suppliers.map(supplier => ({
                      value: supplier.id,
                      label: supplier.name
                    }))}
                    icon={Building2}
                    required
                    placeholder="Select supplier"
                  />

                  <SmartDropdown
                    label="Material Type"
                    name="materialType"
                    value={createForm.materialType}
                    onChange={(e) => setCreateForm({ ...createForm, materialType: e.target.value })}
                    options={materialTypeOptions}
                    icon={Package}
                    required
                    placeholder="Select material type"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={createForm.notes}
                      onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                      placeholder="Any special instructions or notes..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setSelectedWorkflowType(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Starting...' : 'Start Workflow'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowEngineImproved;
